import { useEffect, useRef, useState } from "react";

export type CountdownParts = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  /** Milliseconds left, floored at 0. */
  remainingMs: number;
  isComplete: boolean;
};

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Splits a remaining duration into the four numbers the clock shows.
 *
 * Exported so a test can assert the arithmetic without driving a timer, and
 * so nothing else has to repeat the modulo chain.
 */
export function splitDuration(remainingMs: number): CountdownParts {
  const clamped = Math.max(0, remainingMs);
  const whole = Math.floor(clamped / SECOND) * SECOND;
  return {
    days: Math.floor(whole / DAY),
    hours: Math.floor((whole % DAY) / HOUR),
    minutes: Math.floor((whole % HOUR) / MINUTE),
    seconds: Math.floor((whole % MINUTE) / SECOND),
    remainingMs: clamped,
    isComplete: clamped <= 0,
  };
}

/**
 * The countdown to a fixed instant, re-rendering once a second.
 *
 * Replaces @rumess/react-flip-countdown on the v2 screen. The flip clock came
 * with its own black tile look that the redesign does not use, and it parsed a
 * pre-formatted local-time STRING, which meant the page had to render the same
 * instant twice in two different formats and hope they agreed.
 *
 * `onComplete` fires exactly once per target, including immediately on mount
 * when the target is already past: that is how a viewer who opens the page
 * after the stream is due lands on the player instead of a clock reading all
 * zeros. It is held in a ref so a caller passing an inline arrow function does
 * not restart the interval on every render.
 */
export function useCountdown(
  targetMs: number | null,
  onComplete?: () => void,
): CountdownParts {
  const [now, setNow] = useState(() => Date.now());
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const firedForRef = useRef<number | null>(null);

  useEffect(() => {
    if (targetMs == null) return;
    // Re-read the clock immediately: a tab that was backgrounded for an hour
    // resumes with `now` an hour stale, and waiting a full second to correct
    // it shows a countdown that is visibly wrong on the frame it comes back.
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), SECOND);
    return () => clearInterval(id);
  }, [targetMs]);

  /*
   * No target means the game has no usable date: the caller renders no clock,
   * and this must not report "complete" or it would push that viewer straight
   * past the screen. Zeros with isComplete false is the honest answer.
   */
  const parts =
    targetMs == null
      ? { days: 0, hours: 0, minutes: 0, seconds: 0, remainingMs: 0, isComplete: false }
      : splitDuration(targetMs - now);

  useEffect(() => {
    if (targetMs == null || !parts.isComplete) return;
    if (firedForRef.current === targetMs) return;
    firedForRef.current = targetMs;
    onCompleteRef.current?.();
  }, [targetMs, parts.isComplete]);

  return parts;
}
